"use client";

import Link from "next/link";
import { IdCard, ShieldCheck, Headphones } from "@/components/icons";
import { DOCS, ROUTES, STATS } from "@/lib/constants";
import { scrollToId } from "@/lib/utils";
import { HeroGlow, ReviewBadges } from "@/components/ui";
import TierGrid from "@/components/sections/products/residential-proxies/pricing-tiers/tier-grid";

export default function ResidentialPricingCards({
  asHero = false,
  hideHeader = false,
  compact = false,
}: {
  asHero?: boolean;
  hideHeader?: boolean;
  /** Show a representative slice of tiers with a link to the full pricing page instead of the
   *  complete ladder — used on the product page. */
  compact?: boolean;
}) {
  const handleMoneyBackClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    window.location.hash = "money-back-guarantee";
    scrollToId("money-back-guarantee");
  };

  const sectionPadding = hideHeader
    ? "pt-0 pb-section"
    : asHero
      ? "pt-24 sm:pt-32 pb-16"
      : "py-section";
  // With hideHeader the cards sit flush at the top (pt-0); keep overflow visible so their hover
  // lift + shadow aren't clipped. Otherwise clip the Y axis only: this section sits inside the
  // shell's max-w-7xl frame, so clipping X as well cut the hero's full-bleed ribbon off at the
  // frame's vertical hairlines. HeroGlow's own wrapper already clips the ribbon.
  const overflow = hideHeader ? "overflow-visible" : "overflow-y-clip";

  return (
    <section
      id="pricing-plans"
      className={`full-bleed band-wash border-border relative ${overflow} border-b ${sectionPadding}`}
      {...(hideHeader
        ? { "aria-label": "Pricing plans" }
        : { "aria-labelledby": "residential-pricing-cards-heading" })}
    >
      {asHero && <HeroGlow align="center" />}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {!hideHeader && (
          <div className={`mx-auto max-w-2xl text-center ${asHero ? "mb-10" : "mb-12"}`}>
            {asHero ? (
              <>
                <div
                  className="mb-5 flex flex-wrap items-center justify-center gap-4"
                  aria-label="Review badges"
                >
                  <ReviewBadges />
                </div>
                <h1
                  id="residential-pricing-cards-heading"
                  className="text-foreground mb-5 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
                >
                  Residential Proxy
                  <br />
                  Pricing and Plans
                </h1>
              </>
            ) : (
              <>
                <p className="text-muted-foreground mb-3 text-sm font-medium">Choose Your Plan</p>
                <h2
                  id="residential-pricing-cards-heading"
                  className="text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl"
                >
                  Residential proxy pricing
                </h2>
              </>
            )}
            <p className="text-muted-foreground light:text-foreground/80 text-lg leading-relaxed">
              {asHero
                ? `The only residential proxies you'll ever need — ${STATS.ips} premium residential IPs across ${STATS.countries} countries. Pay per GB, scale as you go. No contracts, no hidden fees.`
                : "Pay per GB. Scale up or down any time with no penalties and no commitments."}
            </p>
            {asHero && (
              <div
                className="mt-7 flex flex-wrap items-center justify-center gap-4"
                aria-label="Trust signals"
              >
                <Link
                  href={ROUTES.kyc}
                  className="border-border bg-muted/40 text-foreground hover:border-brand-green hidden items-center rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors md:flex"
                >
                  <IdCard
                    size={14}
                    className="text-foreground/50 mr-1.5 shrink-0"
                    aria-hidden="true"
                  />
                  KYC required
                </Link>
                <a
                  href="#money-back-guarantee"
                  onClick={handleMoneyBackClick}
                  className="border-border bg-muted/40 text-foreground hover:border-brand-green flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors"
                >
                  <ShieldCheck
                    size={14}
                    className="text-foreground/50 mr-1.5 shrink-0"
                    aria-hidden="true"
                  />
                  30-day money-back guarantee
                  <span className="group/mbtooltip relative ml-0.5 cursor-help">
                    <span
                      className="text-base leading-none"
                      style={{ color: "var(--brand-green)" }}
                    >
                      *
                    </span>
                    <span className="text-foreground bg-popover border-border pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-72 -translate-x-1/2 rounded-lg border px-3 py-2 text-center text-xs leading-relaxed font-normal whitespace-normal normal-case opacity-0 shadow-lg transition-opacity duration-200 group-hover/mbtooltip:opacity-100">
                      All proxies come with a 30-day money-back guarantee conditional upon using no
                      more than 10 GB of data on the individual proxy, up to 2 refunds per account.
                    </span>
                  </span>
                </a>
                <Link
                  href={ROUTES.contactSupport}
                  className="border-border bg-muted/40 text-foreground hover:border-brand-green hidden items-center rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors md:flex"
                >
                  <Headphones
                    size={14}
                    className="text-foreground/50 mr-1.5 shrink-0"
                    aria-hidden="true"
                  />
                  Premium support
                </Link>
              </div>
            )}
          </div>
        )}

        <div className={asHero ? "mx-auto mt-12 max-w-6xl" : "mx-auto max-w-6xl"}>
          <TierGrid compact={compact} />

          <p className="text-muted-foreground mt-6 text-center text-sm">
            Want the full plan details? Read the{" "}
            <a
              href={DOCS.residentialProxies}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground font-medium underline underline-offset-4 transition-opacity hover:opacity-80"
            >
              residential proxy docs
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
}
